'use client'

import React, { useEffect, useRef, useState } from 'react'
import { Popover, Box, Button, Text } from '@mantine/core'

interface HoverMenuProps {
    buttonTitle: string
    children: React.ReactNode
    onButtonClick?: () => void
    disabled?: boolean
    dataTestId?: string
}

const HOVER_CLOSE_DELAY = 220
const OPEN_ANIMATION_DURATION = 200
const OPEN_LOCK_DURATION = OPEN_ANIMATION_DURATION + 80

const HoverMenu: React.FC<HoverMenuProps> = ({
    buttonTitle,
    children,
    onButtonClick,
    disabled = false,
    dataTestId,
}) => {
    const [opened, setOpened] = useState(false)

    const openedRef = useRef(false)
    const isOpeningRef = useRef(false)
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const openLockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        return () => {
            if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
            if (openLockTimerRef.current) clearTimeout(openLockTimerRef.current)
        }
    }, [])

    const cancelPendingClose = () => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current)
            closeTimerRef.current = null
        }
    }

    const open = () => {
        cancelPendingClose()
        if (openedRef.current) return
        openedRef.current = true
        isOpeningRef.current = true
        setOpened(true)
        if (openLockTimerRef.current) clearTimeout(openLockTimerRef.current)
        openLockTimerRef.current = setTimeout(() => {
            isOpeningRef.current = false
        }, OPEN_LOCK_DURATION)
    }

    const close = () => {
        cancelPendingClose()
        if (!openedRef.current) return
        openedRef.current = false
        isOpeningRef.current = false
        if (openLockTimerRef.current) {
            clearTimeout(openLockTimerRef.current)
            openLockTimerRef.current = null
        }
        setOpened(false)
    }

    const scheduleClose = () => {
        cancelPendingClose()
        closeTimerRef.current = setTimeout(close, HOVER_CLOSE_DELAY)
    }

    const navButtonStyles = {
        root: {
            padding: '8px 16px',
            minHeight: 'auto',
            height: 'auto',
            transition: 'all 0.2s ease',
            '&:hover': {
                backgroundColor: 'rgba(45, 26, 255, 0.1)',
                color: '#2D1AFF',
            },
        },
    }

    if (disabled) {
        return (
            <Button variant="subtle" onClick={onButtonClick} data-testid={dataTestId} styles={navButtonStyles}>
                <Text
                    fz="md"
                    fw={500}
                    lh="140%"
                    c="#1F1E1D"
                    style={{
                        transition: 'color 0.2s ease',
                    }}
                >
                    {buttonTitle}
                </Text>
            </Button>
        )
    }

    const handleButtonClick = () => {
        cancelPendingClose()
        if (isOpeningRef.current) {
            onButtonClick?.()
            return
        }
        if (openedRef.current) {
            close()
        } else {
            open()
        }
        onButtonClick?.()
    }

    const handlePopoverChange = (next: boolean) => {
        if (next === openedRef.current) return
        if (!next && isOpeningRef.current) return
        if (next) open()
        else close()
    }

    return (
        <Popover
            opened={opened}
            onChange={handlePopoverChange}
            position="bottom"
            offset={8}
            withArrow={false}
            shadow="md"
            middlewares={{ flip: false, shift: false }}
            transitionProps={{ transition: 'fade', duration: OPEN_ANIMATION_DURATION }}
            styles={{
                dropdown: {
                    padding: 0,
                    border: 'none',
                    width: '100vw',
                    left: '0',
                    right: '0',
                    transform: 'none',
                    zIndex: 1000,
                },
            }}
        >
            <Popover.Target>
                <Button
                    variant="subtle"
                    onClick={handleButtonClick}
                    onMouseEnter={open}
                    onMouseLeave={scheduleClose}
                    data-testid={dataTestId}
                    styles={navButtonStyles}
                >
                    <Text
                        style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: '140%',
                            color: '#1F1E1D',
                            transition: 'color 0.2s ease',
                        }}
                    >
                        {buttonTitle}
                    </Text>
                </Button>
            </Popover.Target>
            <Popover.Dropdown onMouseEnter={cancelPendingClose} onMouseLeave={scheduleClose}>
                <Box
                    bg="#F5F1ED"
                    style={{
                        display: 'flex',
                        padding: '16px 32px 48px 32px',
                        alignItems: 'flex-start',
                    }}
                >
                    <Box
                        style={{
                            maxWidth: '1400px',
                            margin: '0 auto',
                            width: '100%',
                        }}
                    >
                        {React.isValidElement(children)
                            ? React.cloneElement(children, {
                                  ...children.props,
                                  isOpen: opened,
                                  onClose: close,
                              })
                            : children}
                    </Box>
                </Box>
            </Popover.Dropdown>
        </Popover>
    )
}

export default HoverMenu
